import { SIGNATURE_WORDS, type ComponentType } from './ComponentStore'
import { entityIndex, type Entity } from './Entity'
import type { World } from './World'

export interface QueryFilter {
  none?: readonly ComponentType<any>[]
}

/**
 * A cached view over entities matching `all` and lacking `none`.
 *
 * The result array is rebuilt only when the world's structural version changes
 * (an entity created/destroyed, or a component added/removed). Iteration walks
 * the smallest participating store and bit-tests signatures, so adding a rare
 * component to a query makes it cheaper, not more expensive.
 *
 * `entities()` returns a snapshot: destroying entities mid-iteration is safe,
 * but consumers must skip handles that are no longer alive.
 */
export class Query {
  readonly key: string
  private readonly allWords = new Uint32Array(SIGNATURE_WORDS)
  private readonly noneWords = new Uint32Array(SIGNATURE_WORDS)
  private readonly allTypes: readonly ComponentType<any>[]
  /**
   * Double-buffered so `entities()` really is the snapshot its contract
   * promises. Rebuilding in place handed every caller the same array object,
   * so a nested query of the same shape could clear an outer loop's iterator
   * mid-pass — a silent skipped-entity bug waiting for the next refactor.
   */
  private result: Entity[] = []
  private spare: Entity[] = []
  private builtVersion = -1

  constructor(
    private readonly world: World,
    all: readonly ComponentType<any>[],
    filter: QueryFilter = {},
  ) {
    this.allTypes = all
    for (const c of all) this.allWords[c.word] |= c.mask
    for (const c of filter.none ?? []) this.noneWords[c.word] |= c.mask
    this.key = queryKey(all, filter)
  }

  entities(): readonly Entity[] {
    const version = this.world.structuralVersion
    if (version === this.builtVersion) return this.result
    this.rebuild()
    // Swap, so anyone still iterating the previous snapshot keeps a stable array.
    const previous = this.result
    this.result = this.spare
    this.spare = previous
    this.builtVersion = version
    return this.result
  }

  /** Number of matching entities without materialising anything extra. */
  get count(): number {
    return this.entities().length
  }

  /** Convenience for singleton-ish queries (players, arena, …). */
  first(): Entity | undefined {
    return this.entities()[0]
  }

  private rebuild(): void {
    const out = this.spare
    out.length = 0

    if (this.allTypes.length === 0) {
      // Degenerate query: everything alive minus the excluded set.
      for (const e of this.world.liveEntities()) {
        if (this.matchesIndex(entityIndex(e))) out.push(e)
      }
      return
    }

    let smallest = this.world.store(this.allTypes[0])
    for (let i = 1; i < this.allTypes.length; i++) {
      const store = this.world.store(this.allTypes[i])
      if (store.size < smallest.size) smallest = store
    }

    const owners = smallest.owners
    for (let i = 0; i < owners.length; i++) {
      const e = owners[i]
      if (this.matchesIndex(entityIndex(e))) out.push(e)
    }
  }

  private matchesIndex(index: number): boolean {
    const sig = this.world.signatures
    const base = index * SIGNATURE_WORDS
    for (let w = 0; w < SIGNATURE_WORDS; w++) {
      const bits = sig[base + w]
      if ((bits & this.allWords[w]) !== this.allWords[w]) return false
      if ((bits & this.noneWords[w]) !== 0) return false
    }
    return true
  }
}

export function queryKey(
  all: readonly ComponentType<any>[],
  filter: QueryFilter = {},
): string {
  const a = all.map((c) => c.id).sort((x, y) => x - y).join(',')
  const n = (filter.none ?? []).map((c) => c.id).sort((x, y) => x - y).join(',')
  return `${a}!${n}`
}
